<script setup>
	import { ref, onMounted, onUnmounted } from "vue";
	import { useRouter } from 'vue-router'
	import TopicSection from "@/components/about/overview/TopicSection.vue"

	//reactive field proxies
	const activeId = ref(0);
	const topics = ref({})

	//local variables
	const router = useRouter()
	const sectionElements = new Map()

	//Methods	
	function setSectionRef(el, id) {
		if (el)
			sectionElements.set(id, el)
		else
			sectionElements.delete(id)
	}

	let animationFrame = null

	function updateActiveSection() {

	let bestId = null
	let largestVisible = 0

	const viewportHeight = window.innerHeight

		for (const section of topics.value) {

			const el = sectionElements.get(section.id)
			if (!el)
				continue

			const rect = el.getBoundingClientRect()
			const visibleTop = Math.max(rect.top, 0)
			const visibleBottom = Math.min(rect.bottom, viewportHeight)
			const visibleHeight = Math.max(	0, visibleBottom - visibleTop)

			if (visibleHeight > largestVisible) {
				largestVisible = visibleHeight
				bestId = section.id
			}
		}
		if (bestId !== activeId.value) {
			activeId.value = bestId;
		}
	}

	function onScroll() {
		//console.log('scroll')
		if (animationFrame) {
			return
		}

		//debounce scroll event to ensure update action limited to once per browser frame refresh
	 	//console.log('get animation frame')
		animationFrame = requestAnimationFrame(() => {
			animationFrame = null
			updateActiveSection()
		})
	}


	async function runSetup() {
		try {
			const file = import.meta.env.VITE_BASE_URL+ "/json/overview-section.json"
			let x = await fetch(file);
			let y = await x.text();
			topics.value = JSON.parse(y);

			//add id to track topic title
			for (const key in topics.value) {
				const val = topics.value[key];
		        val.id = key
			}
			updateActiveSection()		
		} catch (err) {
			console.log( 'Error retrieving Overview cards',err)
			let msg = 'Error retrieving Overview cards'
			showHomePageError(msg)
			return;	
		}
	}

	runSetup() 

	onMounted(() => {
		window.addEventListener(
			'scroll',
			onScroll,
			{ passive: true }
		)
		window.addEventListener(
			'resize',
			updateActiveSection
		)
	})
		
	onUnmounted(() => {
		window.removeEventListener(
			'scroll',
			onScroll
		)
		window.removeEventListener(
			'resize',
			updateActiveSection
		)

		if (animationFrame)
			cancelAnimationFrame(animationFrame)
		
		// Digital Analytics Program (https://digital.gov/guides/dap) 
        if(import.meta.env.VITE_APP_ENV && import.meta.env.VITE_APP_ENV === 'production') {
            const dapScript = document.createElement("script");
                dapScript.setAttribute("async", "")
                dapScript.setAttribute("id","_fed_an_ua_tag");
                dapScript.setAttribute("type","text/javascript");
                dapScript.setAttribute("src","https://dap.digitalgov.gov/Universal-Federated-Analytics-Min.js?agency=NSF");
            document.head.appendChild(dapScript);
        }

	});

	function showHomePageError(msg) {
		const msgObj = {
			"text": msg.message?msg.message:msg,
			"type": "error"
		}
		router.push({
			name: 'HomeView',
			state: { "msgObj": msgObj } // stored in history.state, not URL
		}) 
	}
	
</script>

<template>
	<div class="container">
		<div class="intro-container">
			<div class="umbrella-title">National Center for Science and Engineering Statistics</div>

    		<div class="title">Indicators Explorer</div>

			<div>
				<div class="separator-line"></div>
			</div>

			<div class="img-container">
				<img src="@/assets/images/overview-400.png" alt="Overview"
					style="width: 100%; height: auto; border-radius: var(--ui-card-Default, .75rem)" />
			</div>
		</div>
				
		<div class="toc-header">
			<h2>ON THIS PAGE</h2>
			<ul>
				<li v-for="(topic, index) in topics" :key="index">
				<a
					:href="`#${topic.id}`"
					:class="{ active: activeId == topic.id && topic.id > 0}">
					<span :class="{ title: topic.id == '0'}">{{ topic.anchor }}</span>
				</a>
				</li>
			</ul>
		</div>

		<div class="section-container">
			<div class="main-content">
				<section
					v-for="(topic, index) in topics"
					:key="topic.id"
					:id="topic.id"
					:ref="el => setSectionRef(el, topic.id)"
					class="page-section" :class="{'alt-color': index % 2 === 0}">
					<TopicSection :topic="topic" />

				</section>
			</div>

			<!-- Sidebar -->
			<aside class="sidebar">
				<h2>ON THIS PAGE</h2>
				<ul>
					<li v-for="(topic, index) in topics" :key="index">
					<a
						:href="`#${topic.id}`"
						:class="{ active: activeId == topic.id && topic.id > 0}">
						<span :class="{ title: topic.id == '0'}">{{ topic.anchor }}</span>
					</a>
					</li>
				</ul>
			</aside>			
		</div>	
	</div>
</template>

<style scoped>

.container {
	background-color: var(--surface-color-Default);	
}

.intro-container {
	padding: var(--spacing-4xl, 3rem);
	background-color: var(--surface-color-Default);	
}
.intro-container .umbrella-title {
    font-family: var(--font-family-Title, Roboto);
    font-size: var(--font-size-H4);
    text-align: center;
    color: var(--text-Header);
    font-weight: var(--font-weight-Bold); /*700*/
    line-height: var(--font-line-height-H4);
    margin-bottom: 0;
  }

.intro-container .title {	
	font-family: var(--font-family-Title, Roboto);
	font-size: var(--font-size-H1);
	font-style: normal;
	/*font-weight: 400; */
	font-weight: var(--font-weight-Bold); /*700*/
	color: var(--text-Header, #171717);
	text-align: center;
	line-height: var(--font-line-height-Hero);
}

.intro-container .sub-title {
	font-family: var(--font-family-Title, Roboto);
	font-size: var(--font-size-Base);
	font-style: normal;
	font-weight: 500;	
	color: var(--text-MedGrey, #2c2c2c);
	text-align: center;
}

.intro-container .img-container {
	text-align: center;
	margin-top: var(--spacing-4xl);
	border-radius: var(--radius-xl);
}

.toc-header {
	display: none;
	background: var(--surface-color-Tertiary);
	padding: var(--spacing-7xl) var(--spacing-4xl);
}
.section-container {
	display: flex;
	min-height: 100vh;
	margin-top: var(--spacing-4xl);
}

.sidebar {
	width: 14.5625rem;
	position: sticky;
	top: 0;
	right: var(--spacing-4xl, 3rem);
	height: 100vh;
	background-color: transparent;
	/*padding: 1rem;*/
	margin-left: -14.5625rem;
}

.sidebar h2 {
	font-weight: bold;
	margin-bottom: 1rem;
}

.sidebar ul, .toc-header ul {
	list-style: none;
	padding: 0;
	margin: 0 -.625rem;
}

.sidebar h2, .toc-header h2 {
	font-family: var(--font-family-Title, Roboto);
	font-size: var(--font-size-Caption);
	font-weight: 500;
} 

.sidebar a, .toc-header a {
	display: block;
	padding: 0.5rem;
	border-radius: var(--radius-md);
	text-decoration: none;
	color: var(--text-MedGrey, #2c2c2c);
	cursor: pointer;
	font-family: var(--font-family-Title, Roboto);
	font-size: var(--font-size-Base);
	font-weight: 400;	
}

.sidebar a .title, .toc-header a .title {
	font-family: var(--font-family-Title, Roboto);
	font-size: var(--font-size-H6);
	font-weight: 500;
}
.sidebar a:hover, .sidebar a:hover .title, 
.toc-header a:hover, .toc-header a:hover .title {
	font-weight: 700;
}

.sidebar a:focus, .toc-header a:focus{
	font-weight: 700;
	outline: 0;
	border: 1.5px solid var(--input-background-Active, #064291)
}

.sidebar a.active, .toc-header a.active {
	font-weight: 700;
	color: #005495;
;
}

.alt-color {
	background-color: var(--surface-color-Tertiary);
}

.separator-line {
	width: 5rem;;
	height: 0.375rem;
	background-color: #005495;
	margin: 1.625rem auto;
}

.main-content {
	flex: 1;
    align-items: flex-start;
    align-self: stretch;
}

/* 1023px - iPad */
@media (max-width: 63.9375rem) {
	.toc-header {
		display: block;
		padding: var(--spacing-xl);
	}

	.toc-header h2 {
		color: var(--text-Primary, #2C2C2C);
		font-size: var(--font-size-Caption, 14px);
		font-weight: 400;
		line-height: var(--font-line-height-Caption, 20px); /* 142.857% */
	}	

	.sidebar {
		display:none;
	}
}
</style>